import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import {
	fireErrorAnalytics,
	fireMsrNotRecommendedAnalytics,
	fireSiteOptedOutAnalytics,
} from '../../services/analytics/index.tsx';
import {
	getOrUpdateFeatureVisited,
	getFeatureVisitedCount,
	withEligibilityCache,
} from '../../services/caching/index.tsx';
import { useNudgeStateV2a } from '../../services/nudge-state/index.tsx';
import { hasProductAccess } from '../../services/permission-check/index.tsx';
import { useFetchIsRequestAccessCapable } from '../../services/request-capabilities/index.tsx';
import { isSite7DaysOld } from '../../services/site-eligibility/index.tsx';
import { isSiteOptedOut } from '../../services/site-opt-out/index.tsx';
import { isNewActiveUser } from '../../services/user-eligibility/index.tsx';
import { MAX_FEATURE_VISIT_COUNT } from '../nudges/common.tsx';
import { useOverrides } from '../../services/overrides/index.tsx';
import { isExcludedForNav4PlacementAA } from '../../services/cjn-nav4-placement/isExcludedForNav4PlacementAA.tsx';
import { isExcludedForNav4Placement } from '../../services/cjn-nav4-placement/isExcludedForNav4Placement.tsx';
import type { EligibilityCheck } from './types.tsx';

/*
  Eligibility can be overriden using the following query parameter:

  ?cjn_conf_overrides={
    "is_your_work_route": false,
    "site_has_no_licensed_confluence": false,
    "feature_visited_too_recently": false,
    "site_is_opted_out": false,
    "user_already_has_confluence": false,
    "user_cannot_request_confluence_access": false,
    "confluence_is_too_new": false,
    "user_is_too_new": false,
    "nudge_already_dismissed": false,
    "nudge_seen_too_many_times": false
  }
*/
type OverrideKey =
	| 'is_your_work_route'
	| 'site_has_no_licensed_confluence'
	| 'feature_visited_too_recently'
	| 'site_is_opted_out'
	| 'user_already_has_confluence'
	| 'user_cannot_request_confluence_access'
	| 'confluence_is_too_new'
	| 'user_is_too_new'
	| 'nudge_already_dismissed'
	| 'nudge_seen_too_many_times';

type UseConfluenceCrossJoinNudgeEligibilityProps = {
	getMSRNudgeRecommendations: () => Promise<string[]>;
};

export const useConfluenceCrossJoinNudgeEligibility = ({
	getMSRNudgeRecommendations,
}: UseConfluenceCrossJoinNudgeEligibilityProps): (() => Promise<
	EligibilityCheck<{ featureVisitedCount: number }>
>) => {
	const cloudId = useCloudId();
	const { getNudgeState: getNudgeStateV2a } = useNudgeStateV2a();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const userId = useAccountId() || '';
	const licensedProducts = useLicensedProducts();
	const isYourWorkRoute = useIsActiveRoute('your-work');
	const withOverrides = useOverrides<OverrideKey>('cjn_conf_overrides');
	const fetchIsRequestAccessCapable = useFetchIsRequestAccessCapable('crossJoinNudgesConfluence');

	return useCallback(async (): Promise<EligibilityCheck<{ featureVisitedCount: number }>> => {
		try {
			const cachedIsSiteOptedOut = withEligibilityCache('site_is_opted_out', isSiteOptedOut, 0.2);
			const cachedHasProductAccess = withEligibilityCache(
				'user_already_has_confluence',
				hasProductAccess,
				0.2,
			);
			const cachedUserCanRequestAccess = withEligibilityCache(
				'user_cannot_request_confluence_access',
				fetchIsRequestAccessCapable,
				0.2,
			);

			if (withOverrides('is_your_work_route', isYourWorkRoute)) {
				return { isEligible: false, ineligibleReason: 'is_your_work_route' };
			}

			if (withOverrides('site_has_no_licensed_confluence', !licensedProducts.confluence)) {
				return { isEligible: false, ineligibleReason: 'site_has_no_licensed_confluence' };
			}

			if (
				withOverrides(
					'feature_visited_too_recently',
					getOrUpdateFeatureVisited('crossJoinNudgesConfluence', 7),
				)
			) {
				return { isEligible: false, ineligibleReason: 'feature_visited_too_recently' };
			}

			// I moved this to the top as most of the users have already seen all their nudges
			// and thus preventing calling other APIs unnecessarily
			const featureVisitedCount = await getFeatureVisitedCount(userId);
			if (
				withOverrides('nudge_seen_too_many_times', featureVisitedCount >= MAX_FEATURE_VISIT_COUNT)
			) {
				return { isEligible: false, ineligibleReason: 'nudge_seen_too_many_times' };
			}

			if (withOverrides('site_is_opted_out', await cachedIsSiteOptedOut(cloudId))) {
				fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesConfluence');
				return { isEligible: false, ineligibleReason: 'site_is_opted_out' };
			}

			const userHasProductAccess = await cachedHasProductAccess('confluence', cloudId);
			if (withOverrides('user_already_has_confluence', userHasProductAccess)) {
				return { isEligible: false, ineligibleReason: 'user_already_has_confluence' };
			}

			const userCanRequestAccessToConfluence = await cachedUserCanRequestAccess(
				'confluence',
				cloudId,
			);
			if (
				withOverrides('user_cannot_request_confluence_access', !userCanRequestAccessToConfluence)
			) {
				return { isEligible: false, ineligibleReason: 'user_cannot_request_confluence_access' };
			}

			if (withOverrides('confluence_is_too_new', !(await isSite7DaysOld('confluence', cloudId)))) {
				return { isEligible: false, ineligibleReason: 'confluence_is_too_new' };
			}

			if (withOverrides('user_is_too_new', await isNewActiveUser(cloudId, userId))) {
				return { isEligible: false, ineligibleReason: 'user_is_too_new' };
			}

			// Since V2A Rollout to 100%, all nudge will use V2A Logic
			if (withOverrides('nudge_already_dismissed', (await getNudgeStateV2a()) === 'DISMISSED')) {
				return { isEligible: false, ineligibleReason: 'nudge_already_dismissed' };
			}

			await isExcludedForNav4PlacementAA(cloudId, userId, createAnalyticsEvent);
			if (await isExcludedForNav4Placement(cloudId, userId, createAnalyticsEvent, 'confluence')) {
				return { isEligible: false, ineligibleReason: 'excluded_for_nav4_placement_experiment' };
			}

			const recommendations = await getMSRNudgeRecommendations();

			if (!recommendations.includes('cjn-jira-conf-v2')) {
				fireMsrNotRecommendedAnalytics(
					createAnalyticsEvent,
					'crossJoinNudgesConfluence',
					'cjn-jira-conf-v2',
				);
				return { isEligible: false, ineligibleReason: 'msr_nudge_not_recommended' };
			}

			return { isEligible: true, data: { featureVisitedCount } };

			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('useCrossJoinNudges', error, { cloudId });
			return { isEligible: false, ineligibleReason: 'unexpected error' };
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		fetchIsRequestAccessCapable,
		getNudgeStateV2a,
		isYourWorkRoute,
		licensedProducts.confluence,
		userId,
		withOverrides,
		getMSRNudgeRecommendations,
	]);
};
